import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLoginUserMutation, useVerifyUserMutation } from '../redux/apiSlice';
import Button from '../components/common/Button';
import { useDispatch } from 'react-redux';
import { login } from '../redux/counterSlice';

interface RouteParams extends Record<string, string | undefined> {
  userId?: string;
  username?: string;
}

const VerifyPage: React.FC = () => {
  const [isVerified, setIsVerified] = useState(false);
  const [verifyUser, { isLoading: verifyLoading }] = useVerifyUserMutation();
  const [loginUser, { isLoading: loginLoading }] = useLoginUserMutation();
  const { userId, username } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const userIdAsNumber = userId ? Number(userId) : 0;
  const name = searchParams.get('name');

  const handleVerify = async () => {
    if (!username) {
      toast.error('Missing username in the URL');
      return;
    }

    try {
      const response = await verifyUser({ userIdAsNumber, username }).unwrap();
      toast.success('Verification successful!');
      setIsVerified(true);
      // setPassword(response.message);
      navigate('/payment', {
        state: {
          userDetails: response,
          subscriptionType: response.subscription.type,
        },
      });
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || 'An error occurred. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleLogin = async () => {
    try {
      const loginResult = await loginUser({
        email: username ?? '',
        password: password ?? '',
      }).unwrap();

      dispatch(
        login({
          accessToken: loginResult.access,
          refreshToken: loginResult.refresh,
        }),
      );

      toast.success('You are successfully logged in!');
      navigate('/courses');
    } catch (err: any) {
      const errorMessage =
        err?.data?.message || 'An error occurred. Please try again.';
      toast.error(errorMessage);
    }
  };

  const handleClick = async () => {
    if (isVerified) {
      // await handleLogin();
      navigate('/login');
    } else {
      await handleVerify();
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-[75vh] bg-gray-100">
      <div className="bg-white shadow-lg rounded-lg w-full max-w-md p-8 border border-gray-200">
        <h2 className="text-2xl font-semibold mb-4 text-center">
          Welcome to My Code Dragon {name || username}
        </h2>

        <Button
          onClick={handleClick}
          disabled={verifyLoading || loginLoading}
          className={`w-full py-2 px-4 rounded-md ${
            verifyLoading || loginLoading
              ? 'bg-gray-400 cursor-not-allowed'
              : isVerified
                ? 'bg-green-600 text-white hover:bg-green-500'
                : 'bg-blue-600 text-white hover:bg-blue-500'
          } transition-colors duration-200`}
        >
          {verifyLoading || loginLoading
            ? 'Processing...'
            : isVerified
              ? 'Start Coding'
              : 'Verify'}
        </Button>
      </div>
      <ToastContainer />
    </div>
  );
};

export default VerifyPage;
